import React, { ReactElement, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { ButtonTW } from 'tailwindComponents/buttonTW/ButtonTW';
import { SpinnerTW } from 'tailwindComponents/spinnerTW/SpinnerTW';
import { DisbursementRollupContext } from './disbursements/DisbursementRollupContext';
import { IDisbursementRollupProps } from './IDisbursementRollupProps';
import { assertDefined } from 'utilities/Assertions';
import { useGetSectionDataQuery } from 'API/order/fees/FeeApi';
import { ScrollToFirstError } from './ScrollToFirstError';
import { Prompt, useHistory, useParams } from 'react-router-dom';
import { FeeTabs, clientUrls } from 'ui-toolkit/rest/ClientUrlFactory';
import { KeyboardShortcut } from 'components/shared/InteractionManagement/KeyboardShortcut';

export interface ILineItemSaveManagerProps {
  actionText: string;
  lineItemId: string;

  // Line Item Save
  submitForm: () => Promise<void>;

  dirty: boolean;
  container: HTMLElement | null;

  children: ({ SaveButtons }: { SaveButtons: ReactElement }) => ReactNode;
}

export const LineItemSaveManager: React.FC<ILineItemSaveManagerProps> = ({
  container,
  lineItemId,
  actionText,
  dirty,
  submitForm,
  children,
}) => {
  const { orderId, section, feeId } = useParams<{ orderId: string; section: FeeTabs; feeId: string }>();
  const history = useHistory();
  const onClose = useCallback(() => {
    history.push(clientUrls.urlFor.feesTab(orderId, section));
  }, [orderId, section, history]);
  const [doClose, setDoClose] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const disbursementRollup = useContext(DisbursementRollupContext);

  const rollupList = Object.values(disbursementRollup)
    .filter((t) => t?.dirty)
    .map((t) => t as IDisbursementRollupProps);

  const dirtyRollup = dirty || rollupList.length > 0;

  assertDefined(orderId);
  const { isLoading: feeIsLoading } = useGetSectionDataQuery(orderId);
  const isLoading = isSubmitting || feeIsLoading;

  const saveProcess = useCallback(
    async (close: boolean = false) => {
      setIsSubmitting(true);
      setDoClose(close);

      if (dirty) {
        await submitForm();
      }
      await Promise.all(rollupList.map((t) => t.onSubmit()));

      setIsSubmitting(false);
    },
    [dirty, rollupList, submitForm]
  );

  const saveAndCloseProcess = useCallback(async () => {
    await saveProcess(true);
  }, [saveProcess]);

  useEffect(() => {
    // This is when they click X and Close
    // doClose will be true so we wait for submitting to be done and for it to be not dirty (fully saved)
    // Now, for new fees, they are always dirty because we can't reload them from the server to be clean. So we wait for the lineItemId to be different from the feeId
    if (doClose && !isSubmitting && (!dirtyRollup || lineItemId !== feeId)) {
      onClose();
    }
  }, [doClose, onClose, dirtyRollup, feeId, isSubmitting, lineItemId]);

  useEffect(() => {
    // There is a special case, where a new fee is becoming editable. In that case we finish saving then move to that new page
    //  Line Item Id will be changed as soon as they save successfully, and then will be different from the feeid which
    //  in the case of new items, will be the section definition id.
    if (!doClose && !isSubmitting && lineItemId !== feeId) {
      history.push(clientUrls.urlFor.feesTabEdit(orderId, section, lineItemId));
    }
  }, [doClose, history, lineItemId, feeId, section, orderId, isSubmitting]);
  const alertUser = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    e.returnValue = '';
  };
  const stopNavigation = dirtyRollup && lineItemId === feeId;
  useEffect(() => {
    if (stopNavigation) {
      window.addEventListener('beforeunload', alertUser);
      return () => {
        window.removeEventListener('beforeunload', alertUser);
      };
    }
    return () => {};
  }, [stopNavigation]);
  return (
    <div>
      <Prompt when={stopNavigation} message={'Pending changes will not be saved'} />
      {children({
        SaveButtons: (
          <div
            className={'fixed bottom-0 right-8 z-10 mt-4 flex flex-row-reverse justify-start gap-4  p-4 backdrop-blur'}
          >
            <KeyboardShortcut triggerKey={'s'} onAction={() => saveProcess(false)} />
            <ScrollToFirstError isSubmitting={isSubmitting} container={container} />
            {isLoading && <SpinnerTW />}
            {!isLoading && (
              <>
                <ButtonTW
                  disabled={isSubmitting || !dirtyRollup}
                  onClick={saveAndCloseProcess}
                  data-test-id={'save-and-close'}
                >
                  {actionText} And Close
                </ButtonTW>
                <ButtonTW secondary disabled={isSubmitting || !dirtyRollup} onClick={() => saveProcess(false)}>
                  {actionText}
                </ButtonTW>
                <ButtonTW ghost secondary onClick={onClose}>
                  {!dirtyRollup ? 'Close' : 'Cancel'}
                </ButtonTW>
              </>
            )}
          </div>
        ),
      })}
    </div>
  );
};
